// Idempotent global listeners that auto-track common interactions.
// Mounted once from App.js. Safe to call multiple times (uses a singleton flag).

import { trackOutboundClick, trackScrollDepth, trackTimeOnPage } from './analytics';

let installed = false;

const SCROLL_MILESTONES = [25, 50, 75, 100];
const TIME_MILESTONES_SEC = [10, 30, 60, 180];

const isBrowser = typeof window !== 'undefined';

const isExternalHref = (href) => {
  if (!href) return false;
  if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;
  try {
    const url = new URL(href, window.location.origin);
    return url.host && url.host !== window.location.host;
  } catch (_e) {
    return false;
  }
};

const closestAnchor = (target) => {
  let el = target;
  while (el && el.nodeType === 1 && el.tagName !== 'A') el = el.parentNode;
  return el && el.tagName === 'A' ? el : null;
};

export const install = () => {
  if (installed || !isBrowser) return;
  installed = true;

  // --- Outbound link clicks ------------------------------------------
  // Single delegated listener for the whole document instead of one per link.
  document.addEventListener('click', (e) => {
    const a = closestAnchor(e.target);
    if (!a) return;
    const href = a.getAttribute('href');
    if (!isExternalHref(href)) return;
    trackOutboundClick({
      href: a.href,
      label: (a.getAttribute('aria-label') || a.textContent || '').trim().slice(0, 80),
    });
  }, { capture: true, passive: true });

  // --- Scroll depth --------------------------------------------------
  // Reports the highest milestone reached per route. Resets on route change.
  let reachedScrollMilestones = new Set();
  let scrollTicking = false;

  const onScroll = () => {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1) * 100;
      SCROLL_MILESTONES.forEach((m) => {
        if (scrolled >= m && !reachedScrollMilestones.has(m)) {
          reachedScrollMilestones.add(m);
          trackScrollDepth(m);
        }
      });
      scrollTicking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  // --- Time on page --------------------------------------------------
  let routeStart = Date.now();
  let reachedTimeMilestones = new Set();
  const timeInterval = setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    const sec = Math.round((Date.now() - routeStart) / 1000);
    TIME_MILESTONES_SEC.forEach((m) => {
      if (sec >= m && !reachedTimeMilestones.has(m)) {
        reachedTimeMilestones.add(m);
        trackTimeOnPage(m);
      }
    });
  }, 5000);

  // Reset milestones on SPA route change (BrowserRouter dispatches popstate
  // but we need pushState too; wrap history.pushState once).
  const wrapHistory = (method) => {
    const original = window.history[method];
    if (original.__tnWrapped) return;
    const wrapped = function wrappedHistoryMethod(...args) {
      const result = original.apply(this, args);
      window.dispatchEvent(new Event('tn:locationchange'));
      return result;
    };
    wrapped.__tnWrapped = true;
    window.history[method] = wrapped;
  };
  wrapHistory('pushState');
  wrapHistory('replaceState');
  window.addEventListener('popstate', () => window.dispatchEvent(new Event('tn:locationchange')));

  window.addEventListener('tn:locationchange', () => {
    reachedScrollMilestones = new Set();
    reachedTimeMilestones = new Set();
    routeStart = Date.now();
  });

  // Cleanup interval if the page is unmounted (e.g. in tests). In a real
  // browser session this just runs for the lifetime of the page.
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => clearInterval(timeInterval), { once: true });
  }
};
